import React, { useMemo, useRef, useState } from 'react';

import { Col, Divider, Empty, Row, Typography } from 'antd';
import classnames from 'classnames';

import { CustomNotification, WithLoading } from 'common/components';

import {
  ProductOverviewMetadata,
  ProductPrimaryInfo,
} from 'pages/product-full-view/components';

import PackageContent from './PackageContent';
import ProductAdvancedModules from './ProductAdvancedModules';
import ProductBaseModules from './ProductBaseModules';

// overlay
import {
  ConditionalProductAdvancedModules,
  ConditionalProductBaseModules,
  ConditionalProductEdit,
  ProductAdvancedModulesOverlay,
  ProductBaseModulesOverlay,
  ProductDetailOverlayEdit,
} from './overlay';

import ProductCustomProperties from './ProductCustomProperties';
import ProductDetailEdit from './ProductDetailEdit';
import QueryProperties, { SearchProductProperties } from './QueryProperties';

import {
  ErrorMessageProperty,
  GroupElementWrapper,
  HeaderGroupElement,
  ModalEditForm,
} from '../../shared/components';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from './ProductDetailContext';

import {
  useScrollToErrorModule,
  useVisibleModal,
} from 'pages/product-full-view/shared/hooks';

import {
  useGetProductItemModules,
  useGetProductItemModulesBrickCode,
  useSaveProductProperties,
} from './queries';

import {
  useCallbackRef,
  useCheckIsProductOverlay,
  useGetProductEnums,
  useGetProductOverlay,
  useGetProductOriginFullViewForProductOverlay,
  useGetProductFullView,
} from 'hooks';

import { useStyleProductDetailContainer } from './layout/useStyleProductDetailContainer';

import ProductHeaderImage from 'common/components/product-header-image/ProductHeaderImage';
import ProductDetailGridItemContainer from './layout/ProductDetailGridItemContainer';
import WithProductDetailExpandBtnfrom, {
  COMPONENT_TYPE,
} from './layout/WithProductDetailExpandBtn';
import {
  IXONECertificationWrapper,
  TYPE_VIEW,
} from 'common/components/product-add/IXONECertificationWrapper';

import { useSyncProductModules } from './hooks';

import {
  filterModuleBrickCode,
  filterModuleProductSchema,
  generateModuleProductToTree,
  uppercaseFirstLetter,
} from 'pages/product-full-view/shared/utils';

import {
  checkFormHasError,
  checkIsAllergenModuleName,
  mappingDataProductByFieldFullPath,
  findFieldFullPathFormData,
  checkTypeOfFormNested,
  filterParamFormValues,
  generateFormValuesForPublishing,
  getFormValues,
  getIdxNameFormOfProperty,
  getNearestFieldFullPathError,
  getNearestFieldPath,
  getPropertyByFieldFullPath,
  groupProductModuleProperties,
  lowercaseProductData,
  validateForm,
} from './utils';

import {
  addModuleNameToDeleteFields,
  getHeaderModules,
  getOriginalDataFormProduct,
  removeModuleNameFromDeleteFields,
} from './overlay/utils';

import { sleep } from 'utils/delay';

import * as productDetailServices from 'services/product';
import {
  ADVANCE_MODULE,
  BASE_MODULE,
  BASIC_FIELD,
  IX_ONE_FIELD,
  TDC_PROPERTIES,
  PRODUCT_SPEC,
  LABELING,
  CLAIMS,
  PET_FOOD,
  FRESH_SEAFOOD,
  FACT_PANEL,
} from 'common/components/product-add/IXONEConstants';

import './ProductDetail.less';
import { isArray } from 'lodash';

const DELAY_TIME_ERROR_FORM = 1000;
const HEADER_BASE_MODULES = 'Base Modules';
const HEADER_ADVANCED_MODULES = 'Advanced Modules';

const ProductDetailView = (props) => {
  const { product, productSchema, onResolveErrorPublicationSuccess } = props;
  const { productId, ownerId } = product;
  // TODO PDK
  const brickCode = 'brickCode';

  const refCurrentFormError = useRef([]);
  const ixoneChangedFieldRef = useRef({});

  const {
    formInstance,
    baseModule: {
      isExpandAll,
      isShowOnlyValue: isShowOnlyValueBase,
      searchText: baseSearchText,
    },
    advancedModule: { searchText: advancedSearchText, isFullView },
    publicationError: { fieldFullPathError, moduleNameError },
    overlay: { deletedFieldsOverlay },
  } = useProductDetail();

  const dispatch = useProductDetailDispatch();

  const [visibleEditModal, { onModal, offModal }] = useVisibleModal();

  const [visiblePackageContent, setVisiblePackageContent] = useState(false);
  const [editedModuleProperties, setEditModuleProperties] = useState({});

  const [statusSubmit, setStatusSubmit] = useState('idle');
  const [statusValidate, setStatusValidate] = useState('idle');

  const { productEnums } = useGetProductEnums();
  const { modulesBrickCode } = useGetProductItemModulesBrickCode(brickCode, {
    enabled: !!brickCode,
  });

  const { handleRefetchProductFullView } = useGetProductFullView({
    productId,
  });

  const {
    dataProductModules,
    isLoading: isLoadingModules,
    statusFetchProductModules,
    refetchDataProductModules,
  } = useGetProductItemModules(productId);

  const isProductOverlay = useCheckIsProductOverlay({
    productId,
  });

  const { originalProductId } = useGetProductOverlay();
  const { dataProductModules: originalProductModules } =
    useGetProductItemModules(originalProductId);

  const { data: originProduct } = useGetProductOriginFullViewForProductOverlay({
    productOverlayId: productId,
    enabled: Boolean(productId && product?.isOverlayProduct),
  });

  const { handleSyncQaSpec, handleSyncAllergens, handleSyncIngredients } =
    useSyncProductModules();
  const productDetailMutation = useSaveProductProperties(productId);

  useScrollToErrorModule({
    statusFetchProductModules,
    moduleNameError,
  });

  const {
    styles,
    dStyles,
    isExpanded,
    productHeaderImageSize,
    searchAdvanceModuleInFullModeIndent,
    toggleTabExpand,
  } = useStyleProductDetailContainer({ brickCode });

  const { baseProductSchema, advancedProductSchema } = useMemo(
    () => filterModuleProductSchema(productSchema),
    [productSchema]
  );

  const getDisplayName = (data, parentName, acc) => {
    data.forEach((properties) => {
      const key = TDC_PROPERTIES[properties.fieldFullPath.toLowerCase()];

      if (key)
        acc[properties.fieldFullPath.toLowerCase()] = [
          ...parentName,
          properties.propertyDisplayName,
        ].join(' - ');

      if (properties?.childProperties?.length)
        getDisplayName(
          properties?.childProperties,
          [...parentName, properties.propertyDisplayName],
          acc
        );
    });
  };

  const basePropertiesModuleNames = baseProductSchema.map((schema) =>
    schema.moduleName.toLowerCase()
  );

  const isEditBasedModule = basePropertiesModuleNames.includes(
    editedModuleProperties?.moduleName?.toLowerCase()
  );

  const brickCodeSchema = useMemo(
    () => filterModuleBrickCode(advancedProductSchema, modulesBrickCode),
    [modulesBrickCode, advancedProductSchema]
  );

  const handleClickEditBtn = async (moduleName) => {
    onModal();

    const foundModuleProperties = productSchema.find(
      (schema) => schema.moduleName === moduleName
    );

    const isEditBasedModule = basePropertiesModuleNames.includes(
      foundModuleProperties?.moduleName?.toLowerCase()
    );

    const foundDataProductModule = dataProductModules?.productItemModules?.find(
      (item) => item.moduleName === moduleName
    );

    const deletedFieldsOverlay =
      foundDataProductModule?.deletedFieldsOverlay ?? [];

    const mappingDeletedFields = removeModuleNameFromDeleteFields({
      deletedFields: deletedFieldsOverlay,
    });

    const formValues = getFormValues(
      lowercaseProductData(foundDataProductModule?.data),
      foundModuleProperties?.moduleProperties
    );

    const fieldFullPathMapping = mappingDataProductByFieldFullPath(
      lowercaseProductData(formValues),
      foundModuleProperties?.moduleProperties
    );

    const searchText = isEditBasedModule ? baseSearchText : advancedSearchText;

    const foundFieldFullPathFormData = findFieldFullPathFormData(
      searchText,
      fieldFullPathMapping
    );

    const editModulePropertiesTemp = groupProductModuleProperties(
      foundModuleProperties
    );
    if (isArray(editModulePropertiesTemp?.moduleGroupProperties)) {
      const findModule = editModulePropertiesTemp?.moduleGroupProperties?.find(
        (val) => val?.nameGroup === 'Other'
      );
      if (findModule && findModule?.groupProperties?.length === 0) {
        editModulePropertiesTemp.moduleGroupProperties =
          editModulePropertiesTemp.moduleGroupProperties.filter(
            (val) => val?.nameGroup !== 'Other'
          );
      }
    }

    setEditModuleProperties(editModulePropertiesTemp);

    dispatch({
      type: productDetailActionTypes.INITIALIZE_DELETED_FIELDS_OVERLAY,
      deletedFieldsOverlay: mappingDeletedFields,
    });

    if (
      moduleName?.toLowerCase() !== searchText?.split('.')[0]?.toLowerCase()
    ) {
      dispatch({
        type: productDetailActionTypes.CLEAR_SEARCH_PROPERTIES,
      });
      dispatch({
        type: productDetailActionTypes.CLEAR_SEARCH_BASE_PROPERTIES,
      });
    }

    dispatch({
      type: productDetailActionTypes.UPDATE_FOUND_FIELD_FULL_PATH,
      payload: {
        isEditBasedModule,
        foundFieldFullPath: foundFieldFullPathFormData,
      },
    });

    // Check error on publication
    if (
      fieldFullPathError &&
      moduleNameError.toLowerCase() === moduleName.toLowerCase()
    ) {
      handleValidatePublication(formValues, foundModuleProperties);
    } else {
      formInstance.setFieldsValue(formValues);
    }
  };

  // Check has error publication -> fieldFullPath
  // STEP 1: Set formValues
  // STEP 1.1: Check current value of form -> what is type of field name (isSingleNestedValue, isMultipleNestedValue, ...)
  // STEP 1.2: Set value to form

  // STEP2: Set errorField
  // Render error field after setting value form. Maybe use setTimeout for better UI?

  const handleValidatePublication = async (
    formValues,
    foundModuleProperties
  ) => {
    const isBaseProperty = basePropertiesModuleNames.includes(
      moduleNameError.toLowerCase()
    );

    const errorPropertyPaths = fieldFullPathError
      .split('.')
      .slice(1)
      .map((path) => uppercaseFirstLetter(path));

    const { moduleProperties } = foundModuleProperties;
    const errorProperty = getPropertyByFieldFullPath(
      moduleProperties,
      fieldFullPathError
    );

    const { isSingleNestedValue } = checkTypeOfFormNested(
      errorProperty?.childProperties,
      errorProperty?.isArray
    );

    const formatErrorPublication = `[Publish Product Error] - ${errorProperty?.propertyDisplayName} cannot be null.`;

    setStatusValidate('validating');

    // Not nested
    if (isBaseProperty) {
      const errorProperty = getPropertyByFieldFullPath(
        moduleProperties,
        fieldFullPathError
      );

      await formInstance.setFieldsValue(formValues);
      await sleep(DELAY_TIME_ERROR_FORM);
      await formInstance.setFields([
        {
          name: errorProperty.propertyName, // Not nested
          errors: [<ErrorMessageProperty errors={[formatErrorPublication]} />],
        },
      ]);
      await formInstance.scrollToField(errorProperty.propertyName, {
        block: 'center',
        behavior: 'smooth',
      });
    } else {
      // ['ParentProperty1', 'ParentProperty2', ..., childProperty]
      const idxNameFormErrorProperty = getIdxNameFormOfProperty({
        properties: moduleProperties,
        paths: errorPropertyPaths,
        fieldFullPathError,
      });
      const { nearestPath } = getNearestFieldPath(
        formValues,
        idxNameFormErrorProperty
      );
      const nearestFieldFullPathError = getNearestFieldFullPathError(
        nearestPath,
        moduleNameError
      );
      const otherFieldFullPath = errorPropertyPaths.filter(
        (path) =>
          !nearestPath.filter((path) => typeof path === 'string').includes(path)
      );
      const nearestFoundProperty = getPropertyByFieldFullPath(
        moduleProperties,
        nearestFieldFullPathError
      );

      const newFormValues = generateFormValuesForPublishing({
        formValues,
        foundProperty: nearestFoundProperty,
        fieldFullPathError,
        nearestPath,
        otherFieldFullPath,
      });

      await formInstance.setFieldsValue(newFormValues);
      await sleep(DELAY_TIME_ERROR_FORM);
      await formInstance.setFields([
        {
          name: idxNameFormErrorProperty,
          errors: [
            isSingleNestedValue ? (
              <Col offset={6}>
                <ErrorMessageProperty errors={[formatErrorPublication]} />
              </Col>
            ) : (
              <ErrorMessageProperty errors={[formatErrorPublication]} />
            ),
          ],
        },
      ]);

      await formInstance.scrollToField(idxNameFormErrorProperty, {
        block: 'center',
        behavior: 'smooth',
      });

      dispatch({
        type: productDetailActionTypes.SEARCH_PROPERTIES,
        payload: {
          searchText: advancedSearchText,
          moduleSchema: [groupProductModuleProperties(foundModuleProperties)],
          modeView: 'edit',
          formInstance: newFormValues, // ???
        },
      });
    }
    setStatusValidate('success');
  };

  const setFormError = (validatingResult) => {
    const errorFormField = validatingResult.map((resultItem) => {
      return {
        name: resultItem.field,
        errors: [resultItem.message],
      };
    });

    refCurrentFormError.current = errorFormField;

    formInstance.setFields(errorFormField);
  };

  const removeFormError = () => {
    if (refCurrentFormError.current?.length === 0) return;

    const removedErrors = refCurrentFormError.current.map((formStatusItem) => ({
      ...formStatusItem,
      errors: [],
    }));

    formInstance.setFields(removedErrors);
  };

  const handleValidate = useCallbackRef(async (formValues = {}) => {
    removeFormError();
    await sleep(500);

    const filterFormValues = filterParamFormValues(
      formValues,
      editedModuleProperties.moduleProperties
    );

    const validatingResult = validateForm({ formValues: filterFormValues });

    if (validatingResult?.length) {
      setFormError(validatingResult);
      return Promise.reject(new Error({ name: 'validation fail' }));
    } else {
      return Promise.resolve(filterFormValues);
    }
  });

  const getOriginalData = () => {
    const foundModuleProperties = productSchema.find(
      (schema) => schema.moduleName === editedModuleProperties?.moduleName
    );

    const foundDataProductModule = dataProductModules?.productItemModules?.find(
      (item) => item.moduleName === editedModuleProperties?.moduleName
    );

    return getFormValues(
      lowercaseProductData(foundDataProductModule?.data),
      foundModuleProperties?.moduleProperties
    );
  };

  const removeModuleNameFromFieldFullPath = (fieldFullPath) => {
    const fieldPathArr = fieldFullPath.split('.');

    if (fieldPathArr?.length === 1) return fieldPathArr[0];

    fieldPathArr.shift();

    return fieldPathArr.join('.');
  };

  const handleSaveProductProperties = async (ixoneIgnore, fieldList) => {
    const formValues = await formInstance.validateFields();

    const validatedFormData = await handleValidate(formValues);
    const fieldsError = await formInstance.getFieldsError();

    if (
      checkFormHasError(fieldsError) &&
      checkIsAllergenModuleName(editedModuleProperties?.moduleName)
    ) {
      CustomNotification.error('Please resolve the errors before submitting!');
    } else {
      const { moduleName } = editedModuleProperties;
      const noModuleNameFieldList = fieldList?.map((fieldFullPath) => {
        return removeModuleNameFromFieldFullPath(fieldFullPath);
      });

      const params = {
        id: productId,
        moduleName,
        data: validatedFormData,
        deletedFields: addModuleNameToDeleteFields({
          deletedFields: deletedFieldsOverlay,
          moduleName,
        }),
        ixOneIgnore: ixoneIgnore,
        IxOneIgnoreFields:
          ixoneIgnore === 'ignore' ? noModuleNameFieldList : undefined,
      };

      productDetailMutation.mutate(params, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            dispatch({
              type: productDetailActionTypes.CLEAR_SEARCH_PROPERTIES,
            });
            handleSyncAllergens(productId);
            handleSyncIngredients(productId);
            handleSyncQaSpec(productId);
            refetchDataProductModules();
            ixoneChangedFieldRef.current = {};
            handleRefetchProductFullView();
            onResolveErrorPublicationSuccess?.();
            offModal();
          }
        },
        onError: (error) => {
          CustomNotification.error(error?.message ?? 'Something went wrong!');
        },
      });
    }
  };

  const isValidatingError = statusValidate === 'validating';

  const handleToggleTabExpand = (value) => {
    toggleTabExpand(value);

    if (isFullView) {
      dispatch({
        type: productDetailActionTypes.TOGGLE_FULL_VIEW,
        payload: {
          value: false,
        },
      });
    }
  };

  return (
    <>
      <div
        className={classnames('product-detail-view', {
          'product-detail-view--expanded': isExpanded && !isFullView,
          'product-detail-view--full': isFullView,
        })}
      >
        <>
          <ProductDetailGridItemContainer gridItemType='product-primary-info'>
            <Row style={dStyles.productPrimary.wrap}>
              {isExpanded && (
                <div style={dStyles.productHeader.imageWrap}>
                  <ProductHeaderImage
                    productDetail={product}
                    productHeaderImageSize={productHeaderImageSize}
                    hidden={!isExpanded}
                    toggleTabExpand={handleToggleTabExpand}
                    toggleExpandText='Open Full Detail View'
                  />
                </div>
              )}
              <Col
                id='product-detail-primary-container'
                style={dStyles.productPrimary.content}
              >
                <ProductPrimaryInfo
                  product={product}
                  originProduct={originProduct}
                />
              </Col>
            </Row>
            {!isExpanded && <Divider style={styles.divider.productPrimary} />}
          </ProductDetailGridItemContainer>

          <ProductDetailGridItemContainer gridItemType='product-meta'>
            <WithProductDetailExpandBtnfrom
              componentType={COMPONENT_TYPE.productMeta}
            >
              <ProductOverviewMetadata
                product={product}
                originProduct={originProduct}
                productItemModules={dataProductModules}
                productSchema={productSchema}
                productEnums={productEnums}
                showPackageDrawer={() => setVisiblePackageContent(true)}
              />
            </WithProductDetailExpandBtnfrom>
            <Divider style={styles.divider.metaData} />
          </ProductDetailGridItemContainer>

          {!isFullView && (
            <>
              <ProductDetailGridItemContainer
                gridItemType='query-property-left'
                style={dStyles.propertyQueryLeft}
              >
                <QueryProperties
                  moduleType='base'
                  moduleSchema={baseProductSchema}
                  isExpanded={isExpanded}
                />
              </ProductDetailGridItemContainer>

              <ProductDetailGridItemContainer
                gridItemType='product-detail-left-module'
                style={{
                  ...styles.detail.leftModule.container,
                  minHeight: 120,
                }}
              >
                <WithLoading loading={isLoadingModules}>
                  <div style={styles.detail.leftModule.content}>
                    <HeaderGroupElement
                      header={getHeaderModules(
                        HEADER_BASE_MODULES,
                        isProductOverlay
                      )}
                      className='product-detail__group-header product-detail__group-header--module'
                    />
                    <GroupElementWrapper className='product-detail-view__group-module'>
                      <ConditionalProductBaseModules>
                        {(isProductOverlay) => {
                          return (
                            <>
                              {isProductOverlay ? (
                                <ProductBaseModulesOverlay
                                  productSchema={baseProductSchema}
                                  customProperties={null}
                                  dataProductModules={dataProductModules}
                                  onClickEditBtn={handleClickEditBtn}
                                  productEnums={productEnums}
                                />
                              ) : (
                                <ProductBaseModules
                                  productSchema={baseProductSchema}
                                  customProperties={
                                    <ProductCustomProperties
                                      hasPermissionEditProperties={true}
                                      productId={productId}
                                      ownerId={ownerId}
                                      queryLeftProperties={{
                                        expandAll: isExpandAll,
                                        onlyShowValues: isShowOnlyValueBase,
                                      }}
                                    />
                                  }
                                  dataProductModules={dataProductModules}
                                  onClickEditBtn={handleClickEditBtn}
                                  productEnums={productEnums}
                                />
                              )}
                            </>
                          );
                        }}
                      </ConditionalProductBaseModules>
                    </GroupElementWrapper>
                  </div>
                </WithLoading>
              </ProductDetailGridItemContainer>
            </>
          )}
        </>

        <ProductDetailGridItemContainer
          gridItemType='query-property-right'
          style={dStyles.propertyQueryRight}
        >
          <WithProductDetailExpandBtnfrom
            componentType={COMPONENT_TYPE.queryPropertyRight}
            onClick={handleToggleTabExpand}
          >
            {brickCode && (
              <div>
                <QueryProperties
                  moduleType='advanced'
                  moduleSchema={brickCodeSchema}
                  isExpanded={isExpanded}
                  isFull={isFullView}
                  searchAdvanceModuleInFullModeIndent={
                    searchAdvanceModuleInFullModeIndent
                  }
                  toggleTabExpand={toggleTabExpand}
                />
              </div>
            )}
          </WithProductDetailExpandBtnfrom>
        </ProductDetailGridItemContainer>

        <ProductDetailGridItemContainer
          gridItemType='product-brickcode-detail'
          style={styles.detail.rightModule.container}
        >
          <WithLoading loading={isLoadingModules}>
            <div style={styles.detail.rightModule.content}>
              <HeaderGroupElement
                header={getHeaderModules(
                  HEADER_ADVANCED_MODULES,
                  isProductOverlay
                )}
                className='product-detail__group-header product-detail__group-header--module'
              />

              <GroupElementWrapper className='product-detail-view__group-module'>
                <ConditionalProductAdvancedModules>
                  {(isProductOverlay) => {
                    return (
                      <>
                        {isProductOverlay ? (
                          <ProductAdvancedModulesOverlay
                            productSchema={brickCodeSchema}
                            onClickEditBtn={handleClickEditBtn}
                            dataProductModules={dataProductModules}
                            productEnums={productEnums}
                          />
                        ) : (
                          <ProductAdvancedModules
                            productSchema={brickCodeSchema}
                            onClickEditBtn={handleClickEditBtn}
                            dataProductModules={dataProductModules}
                            productEnums={productEnums}
                          />
                        )}
                      </>
                    );
                  }}
                </ConditionalProductAdvancedModules>
              </GroupElementWrapper>
            </div>
          </WithLoading>
        </ProductDetailGridItemContainer>
      </div>

      {visibleEditModal && (
        <IXONECertificationWrapper
          showModal
          type={TYPE_VIEW.PRODUCT_DETAIL}
          onConfirm={(ixoneIgnore, fieldList) => {
            handleSaveProductProperties(ixoneIgnore, fieldList);
          }}
          onCancel={() => {
            offModal();
            ixoneChangedFieldRef.current = {};
            dispatch({
              type: productDetailActionTypes.RESET_PRODUCT_DETAIL_STATES,
            });
            dispatch({
              type: productDetailActionTypes.CLEAR_SEARCH_BASE_PROPERTIES,
            });
          }}
        >
          {(handleConfirmIXONE) => (
            <ModalEditForm
              className='product-detail-view__modal'
              title={
                <Row justify='space-between' align='middle'>
                  <Col flex='auto'>
                    <Typography.Text
                      strong
                      style={styles.modalEditForm.moduleNAme}
                    >
                      {editedModuleProperties?.moduleDisplayName}
                    </Typography.Text>
                  </Col>

                  <Col
                    flex='400px'
                    style={styles.modalEditForm.searchProperties}
                  >
                    <SearchProductProperties
                      treeData={generateModuleProductToTree([
                        editedModuleProperties,
                      ])}
                      value={
                        isEditBasedModule ? baseSearchText : advancedSearchText
                      }
                      onChange={(searchText) =>
                        dispatch({
                          type: isEditBasedModule
                            ? productDetailActionTypes.SEARCH_BASE_PROPERTIES
                            : productDetailActionTypes.SEARCH_PROPERTIES,
                          payload: {
                            searchText,
                            moduleSchema: [editedModuleProperties],
                            modeView: 'edit',
                            formInstance,
                          },
                        })
                      }
                    />
                  </Col>
                </Row>
              }
              visible={visibleEditModal}
              onCancel={() => {
                offModal();
                ixoneChangedFieldRef.current = {};
                dispatch({
                  type: productDetailActionTypes.CLEAR_SEARCH_PROPERTIES,
                });
                dispatch({
                  type: productDetailActionTypes.CLEAR_SEARCH_BASE_PROPERTIES,
                });
              }}
              onOk={() => {
                const originalData = getOriginalData();
                handleConfirmIXONE({
                  originalData,
                  changedFields: ixoneChangedFieldRef.current,
                });
              }}
              okButtonProps={{
                // loading: statusSubmit === 'loading',
                loading: productDetailMutation.isLoading,
              }}
            >
              {/* FIXME: Move WithLoading to outside is not working? I don't know about it. Fix later */}
              <WithLoading
                loading={isValidatingError}
                tip='Search for error fields...'
              >
                {/* <ProductDetailEdit
                  ixoneChangedFieldRef={ixoneChangedFieldRef}
                  moduleNameProperties={editedModuleProperties}
                  isGroup={editedModuleProperties.isGroup}
                  productEnums={productEnums}
                  handleValidate={handleValidate}
                  isEditBasedModule={isEditBasedModule}
                /> */}

                <ConditionalProductEdit>
                  {(isProductOverlay) => {
                    return (
                      <>
                        {isProductOverlay ? (
                          <ProductDetailOverlayEdit
                            ixoneChangedFieldRef={ixoneChangedFieldRef} //! need to add new code to component
                            moduleNameProperties={editedModuleProperties}
                            isGroup={editedModuleProperties.isGroup}
                            productEnums={productEnums}
                            handleValidate={handleValidate}
                            isEditBasedModule={isEditBasedModule} //! need to add new code to component
                            originalProductModules={getOriginalDataFormProduct(
                              editedModuleProperties,
                              originalProductModules
                            )}
                          />
                        ) : (
                          <ProductDetailEdit
                            ixoneChangedFieldRef={ixoneChangedFieldRef}
                            moduleNameProperties={editedModuleProperties}
                            isGroup={editedModuleProperties.isGroup}
                            productEnums={productEnums}
                            handleValidate={handleValidate}
                            isEditBasedModule={isEditBasedModule}
                            /* We require product modules to get deletedFields. With a non-overlay product, we'll get value of deleted fields based on the initial data modules  */
                            productModules={getOriginalDataFormProduct(
                              editedModuleProperties,
                              dataProductModules
                            )}
                          />
                        )}
                      </>
                    );
                  }}
                </ConditionalProductEdit>
              </WithLoading>
            </ModalEditForm>
          )}
        </IXONECertificationWrapper>
      )}

      {visiblePackageContent && (
        <PackageContent
          onClose={() => setVisiblePackageContent(false)}
          visible={visiblePackageContent}
        />
      )}
    </>
  );
};

export default ProductDetailView;
